// src/features/urgewalds/new.tsx

import { z } from "zod";

import { Form } from "@/components/ui/form";
import { Card, CardBody, CardFooter, CardHeader, Divider } from "@heroui/react";
import { useTranslation } from "react-i18next";

import {
  AutocompleteAsyncField,
  CheckboxField,
  DatePickerField,
  InputField,
} from "@/features/FormCard/Fields";

import { FormActionsUpdate } from "@/features/FormCard/Actions";
import {
  ConfirmationModal,
  DeleteConfirmationModal,
} from "@/features/FormCard/Modals";

import { useEditForm } from "@/features/Edit/hooks";
import { Diff } from "@/features/FormCard/Diff";

import {
  dateSchema,
  parseToDateValue,
  parseYes,
  preprocessTextAsyncAutocompleteField,
} from "@/features/FormCard/utils";

const FormSchema = z.object({
  // AutocompleteAsync for issuer
  issuer_name: z.preprocess(
    (e: any) => preprocessTextAsyncAutocompleteField(e),
    z
      .union([z.string(), z.object({ id: z.number(), name: z.string() })])
      .optional(),
  ),
  issuer_id: z.preprocess(
    (val) => {
      if (
        val === null ||
        val === undefined ||
        val === "" ||
        val === "nan" ||
        isNaN(Number(val))
      ) {
        return undefined;
      }
      return Number(val);
    },
    z.number({ message: "Issuer is required" }).min(1, "Issuer is required"),
  ),
  // Date picker
  date: z.preprocess((val) => parseToDateValue(val as string), dateSchema),
  // Required numeric fields
  installed_coal_power_capacity: z.coerce.number({
    message: "Installed coal power capacity is required",
  }),
  annual_coal_production: z.coerce.number({
    message: "Annual coal production is required",
  }),
  // Optional numeric fields
  coal_share_power_production: z.coerce.number().optional().nullable(),
  coal_share_revenue: z.coerce.number().optional().nullable(),
  unconventional_production: z.coerce.number().optional().nullable(),
  unconventional_production_fracking: z.coerce.number().optional().nullable(),
  unconventional_production_tar_sands: z.coerce.number().optional().nullable(),
  unconventional_production_coalbed_methane: z.coerce
    .number()
    .optional()
    .nullable(),
  unconventional_production_extra_heavy_oil: z.coerce
    .number()
    .optional()
    .nullable(),
  unconventional_production_ultra_deepwater: z.coerce
    .number()
    .optional()
    .nullable(),
  unconventional_production_arctic: z.coerce.number().optional().nullable(),
  // Boolean field
  is_developer: z.preprocess(
    (val) => parseYes(val as string),
    z.boolean().optional(),
  ),
});

export function UrgewaldsEditCard({
  id,
  client,
  table_name,
}: {
  id: number;
  client: string;
  table_name: string;
}) {
  const { t } = useTranslation();

  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    changedFields,
    handleDelete,
    isDeleting,
  } = useEditForm({
    disable_translations_request: true,
    id,
    client,
    table_name,
    FormSchema,
    adjustOnReset: (key, value, fetchedData) => {
      if (key === "issuer_name") {
        return {
          id: fetchedData.issuer_id,
          name: fetchedData.issuer_name,
          text: fetchedData.issuer_name,
        };
      }
      return value;
    },
  });

  return (
    <div className="grid place-items-center h-full w-full max-w-card">
      <div className="flex justify-center w-full">
        <Card className="p-4 bg-white w-full">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader className="text-lg font-bold text-gray-700">
                Urgewalds {id}
              </CardHeader>
              <Divider />
              <CardBody className="flex flex-col gap-4">
                {/* Row 1: Issuer + Date */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <AutocompleteAsyncField
                    name="issuer_name"
                    label={t(`${table_name}.columns.issuer_name`, {
                      ns: "tables",
                      defaultValue: "Issuer",
                    })}
                    base_url="instit-quantilia/issuers"
                    column_name="name"
                    id_column="issuer_id"
                    control={form.control}
                    isRequired
                  />
                  <DatePickerField
                    name="date"
                    label={t(`${table_name}.columns.name`, {
                      ns: "tables",
                      defaultValue: "Date",
                    })}
                    control={form.control}
                    isRequired
                  />
                </div>

                {/* Row 2: Installed Coal Power Capacity + Annual Coal Production */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="installed_coal_power_capacity"
                    label={t(
                      `${table_name}.columns.installed_coal_power_capacity`,
                      {
                        ns: "tables",
                        defaultValue: "Installed Coal Power Capacity",
                      },
                    )}
                    type="number"
                    control={form.control}
                    isRequired
                  />
                  <InputField
                    name="annual_coal_production"
                    label={t(`${table_name}.columns.annual_coal_production`, {
                      ns: "tables",
                      defaultValue: "Annual Coal Production",
                    })}
                    type="number"
                    control={form.control}
                    isRequired
                  />
                </div>

                {/* Row 3: Coal Share Power Production + Coal Share Revenue */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="coal_share_power_production"
                    label={t(
                      `${table_name}.columns.coal_share_power_production`,
                      {
                        ns: "tables",
                        defaultValue: "Coal Share Power Production",
                      },
                    )}
                    type="number"
                    control={form.control}
                  />
                  <InputField
                    name="coal_share_revenue"
                    label={t(`${table_name}.columns.coal_share_revenue`, {
                      ns: "tables",
                      defaultValue: "Coal Share Revenue",
                    })}
                    type="number"
                    control={form.control}
                  />
                </div>

                {/* Row 4: Unconventional Production + Fracking */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="unconventional_production"
                    label={t(
                      `${table_name}.columns.unconventional_production`,
                      {
                        ns: "tables",
                        defaultValue: "Unconventional Production",
                      },
                    )}
                    type="number"
                    control={form.control}
                  />
                  <InputField
                    name="unconventional_production_fracking"
                    label={t(
                      `${table_name}.columns.unconventional_production_fracking`,
                      {
                        ns: "tables",
                        defaultValue: "Unconventional Production Fracking",
                      },
                    )}
                    type="number"
                    control={form.control}
                  />
                </div>

                {/* Row 5: Tar Sands + Coalbed Methane */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="unconventional_production_tar_sands"
                    label={t(
                      `${table_name}.columns.unconventional_production_tar_sands`,
                      {
                        ns: "tables",
                        defaultValue: "Unconventional Production Tar Sands",
                      },
                    )}
                    type="number"
                    control={form.control}
                  />
                  <InputField
                    name="unconventional_production_coalbed_methane"
                    label={t(
                      `${table_name}.columns.unconventional_production_coalbed_methane`,
                      {
                        ns: "tables",
                        defaultValue:
                          "Unconventional Production Coalbed Methane",
                      },
                    )}
                    type="number"
                    control={form.control}
                  />
                </div>

                {/* Row 6: Extra Heavy Oil + Ultra Deepwater */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="unconventional_production_extra_heavy_oil"
                    label={t(
                      `${table_name}.columns.unconventional_production_extra_heavy_oil`,
                      {
                        ns: "tables",
                        defaultValue:
                          "Unconventional Production Extra Heavy Oil",
                      },
                    )}
                    type="number"
                    control={form.control}
                  />
                  <InputField
                    name="unconventional_production_ultra_deepwater"
                    label={t(
                      `${table_name}.columns.unconventional_production_ultra_deepwater`,
                      {
                        ns: "tables",
                        defaultValue:
                          "Unconventional Production Ultra Deepwater",
                      },
                    )}
                    type="number"
                    control={form.control}
                  />
                </div>

                {/* Row 7: Arctic + Is Developer */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="unconventional_production_arctic"
                    label={t(
                      `${table_name}.columns.unconventional_production_arctic`,
                      {
                        ns: "tables",
                        defaultValue: "Unconventional Production Arctic",
                      },
                    )}
                    type="number"
                    control={form.control}
                  />
                  <CheckboxField
                    name="is_developer"
                    label={t(`${table_name}.columns.is_developer`, {
                      ns: "tables",
                      defaultValue: "Is Developer",
                    })}
                    control={form.control}
                  />
                </div>
              </CardBody>

              <CardFooter>
                <FormActionsUpdate
                  onReset={handleReset}
                  onDelete={openDeleteModal}
                  isDirty={form.formState.isDirty}
                  elementId={id}
                  client={client}
                  allowDelete={false}
                />
              </CardFooter>
            </form>
          </Form>

          {/* Confirmation before creating */}
          <ConfirmationModal
            isOpen={isConfirmationModalOpen}
            isLoading={isUpdating}
            onClose={cancelUpdate}
            onConfirm={confirmUpdate}
            title={t("confirm_update")}
          >
            {changedFields
              ? Diff(changedFields, ["issuer_id"])
              : "No changes detected"}
          </ConfirmationModal>

          {/* Cancel/new-list navigation */}
          <DeleteConfirmationModal
            isOpen={isDeleteModalOpen}
            onClose={closeDeleteModal}
            onDelete={handleDelete}
            isLoading={isDeleting}
          />
        </Card>
      </div>
    </div>
  );
}
